import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Cloud, Camera, Shield, Users } from 'lucide-react';
import { aboutContent } from '../data/mock';
import { apiFetch } from '../lib/api';

const SHOW_LEGACY_SUPPORTING_IMAGES = false;
const LEGACY_SUPPORTING_IMAGES = [
  {
    src: '/media/site_image1.jpg',
    alt: 'Heartland Plains Chasing team in the field'
  },
  {
    src: '/media/site_image2.jpg',
    alt: 'Storm structure in the Heartland Plains Chasing coverage area'
  }
];

const AboutSection = () => {
  const [teamMemberCount, setTeamMemberCount] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadTeamMemberCount = async () => {
      try {
        const response = await apiFetch('/api/team/members?limit=200&auto_import=true');
        if (!response.ok) {
          return;
        }

        const members = await response.json();
        if (isMounted && Array.isArray(members)) {
          setTeamMemberCount(members.length);
        }
      } catch {
        // Keep unknown count if backend is unavailable.
      }
    };

    loadTeamMemberCount();
    return () => {
      isMounted = false;
    };
  }, []);

  const features = [
    {
      icon: Cloud,
      title: 'Storm Documentation',
      description: 'Capturing severe weather phenomena'
    },
    {
      icon: Camera,
      title: 'Photography',
      description: 'Storm photography and videography'
    },
    {
      icon: Shield,
      title: 'Safety First',
      description: 'Strict safety protocols for all chase operations'
    },
    {
      icon: Users,
      title: 'Community',
      description: 'Awareness for severe weather safety'
    }
  ];

  return (
    <section id="about" className="py-24 bg-black relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-red-500 rounded-full filter blur-[128px]" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gray-500 rounded-full filter blur-[128px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ amount: 0.2 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block px-4 py-2 bg-red-500/20 text-red-400 rounded-full text-sm font-medium mb-6">
              Our Story
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {aboutContent.title}
            </h2>
            <p className="text-xl text-gray-400 mb-8">
              {aboutContent.subtitle}
            </p>
            <div className="text-gray-300 leading-relaxed space-y-4">
              {aboutContent.description.split('\n\n').map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </motion.div>

          {/* Right Features + Supporting Images */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ amount: 0.2 }}
            transition={{ duration: 0.8 }}
            className="flex flex-col gap-6"
          >
            {SHOW_LEGACY_SUPPORTING_IMAGES && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ amount: 0.2 }}
                transition={{ delay: 0.1 }}
                className="hidden lg:block rounded-2xl overflow-hidden border border-red-500/30 bg-gray-900/60"
              >
                <img
                  src={LEGACY_SUPPORTING_IMAGES[0].src}
                  alt={LEGACY_SUPPORTING_IMAGES[0].alt}
                  className="w-full h-84 xl:h-96 object-cover object-center"
                />
              </motion.div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ amount: 0.2 }}
                  transition={{ delay: index * 0.15 }}
                  className="group min-w-0 p-6 bg-gray-900/50 border border-gray-800 rounded-2xl hover:border-red-500/50 hover:bg-gray-900/80 transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-red-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-red-500/30 transition-colors duration-300">
                    <feature.icon className="text-red-400" size={28} />
                  </div>
                  <h3 className="text-white font-bold text-lg mb-2 break-words">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 text-sm break-words">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>

            {SHOW_LEGACY_SUPPORTING_IMAGES && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ amount: 0.2 }}
                transition={{ delay: 0.25 }}
                className="hidden lg:block rounded-2xl overflow-hidden border border-red-500/30 bg-gray-900/60"
              >
                <img
                  src={LEGACY_SUPPORTING_IMAGES[1].src}
                  alt={LEGACY_SUPPORTING_IMAGES[1].alt}
                  className="w-full h-84 xl:h-96 object-cover object-center"
                />
              </motion.div>
            )}
          </motion.div>
        </div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ amount: 0.2 }}
          transition={{ delay: 0.3 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-3 gap-8"
        >
          {[
            { value: '2026', label: 'Established' },
            { value: teamMemberCount === null ? '--' : String(teamMemberCount), label: 'Team Members' },
            { value: 'KS', label: 'Home State' }
          ].map((stat, index) => (
            <div key={stat.label} className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ amount: 0.2 }}
                transition={{ delay: index * 0.1, type: 'spring' }}
                className="text-4xl md:text-5xl font-bold text-red-500 mb-2"
              >
                {stat.value}
              </motion.div>
              <p className="text-gray-400 font-medium">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
